"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  Label,
  MultipleSelect,
  CustomDatePicker,
  Textarea,
  Button,
  Input,
} from "@int-traderjoes/ui-components-library";
import { isValid } from "date-fns";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";
import {
  clampDiscontinuationDateToAllowedRange,
  DISCONTINUATION_DATE_FORMAT,
  formatDiscontinuationDate,
  getDefaultDiscontinuationDateString,
  getDiscontinuationMaxSelectableDate,
  isDiscontinuationDateWithinAllowedRange,
  isDiscontinuationReasonOther,
  parseDiscontinuationDateValue,
  parseDiscontinuationReasonSelections,
  serializeDiscontinuationReasonSelections,
} from "@/components/ActiveProjects/shared/utils/discontinuationDateRules";
import { formatValuesForMultipleSelect } from "@/utils/helper";
import useFileManagement from "@/hooks/useFileManagement";
import { useFileValidator } from "@/hooks/useValidateFile";
import { DEFAULT_FILE_SIZE } from "@/utils/constants";
import { FILE_VALIDATION_CONFIG } from "@/utils/fileValidationConstants";
import DocumentsUploadList, {
  type DocumentsUploadItem,
} from "./DocumentsUploadList";
import DiscontinuationProductSummaryView, {
  hasDiscontinuationSummaryText,
} from "./DiscontinuationProductSummaryView";
import {
  type DiscontinuationFormSectionVisibility,
} from "./config/discontinuationWorkflowModalConfig";
import { resolveDiscontinuationNoBuyerAssignedBannerMessage } from "@/components/ActiveProjects/shared/utils/discontinuationWorkflowModalLabels";
import Alert from "@/components/Alert";

export const DISCONTINUATION_DOCUMENTS_FIELD_KEY = "discontinuationDocuments";

const DISCONTINUATION_UPLOAD_FIELD = "discontinuationDocuments";

const NOTES_MAX_LENGTH = 1000;

export type DiscontinuationDocumentItem = {
  id: string | number | null;
  name: string | null;
};

export type DiscontinuationInformationFormValues = {
  discontinuationDate: string;
  reasonForDiscontinuation: string;
  notes: string;
  documents: DiscontinuationDocumentItem[];
};

export type DiscontinuationReasonOption = {
  label: string;
  value: string;
};

export type DiscontinuationProductSummary = {
  productName?: string;
  sku?: string;
  vendorName?: string;
  buyerName?: string;
};

export type DiscontinuationInformationFormLayout = "modal" | "section";

export type DiscontinuationInformationFormProps = {
  values: DiscontinuationInformationFormValues;
  onChange: (patch: Partial<DiscontinuationInformationFormValues>) => void;
  reasonOptions: DiscontinuationReasonOption[];
  /** `"modal"` — date then reason (stacked). `"section"` — reason and date in two columns. */
  layout?: DiscontinuationInformationFormLayout;
  darkModeClass?: string;
  disabled?: boolean;
  /** `"picker"` (default) or `"text"` — read-only text input for info sections. */
  discontinuationDateDisplayMode?: "picker" | "text";
  /**
   * Ready to Order status change date (from API). Reserved for future use;
   * discontinuation date is only capped at today (no minimum).
   */
  readyToOrderStatusChangeDate?: string | number | Date | null;
  /** @deprecated Notes are required only when reason is Other. */
  requireNotes?: boolean;
  requireDocuments?: boolean;
  /** Shown above fields when opening the discontinue-product modal */
  productSummary?: DiscontinuationProductSummary | null;
  documentsUploadPlaceholder?: string;
  showDocumentUpload?: boolean;
  /** @deprecated Download is always shown (packaging documents list pattern). */
  showDownloadOnDocuments?: boolean;
  fieldIdPrefix?: string;
  labels?: Partial<{
    discontinuationDate: string;
    reasonForDiscontinuation: string;
    notesForDiscontinuation: string;
    buyerNotesForCancelDiscontinuation: string;
    documents: string;
    product: string;
    vendor: string;
    buyerName: string;
  }>;
  /** Optional footer (e.g. Cancel / Initiate discontinuation). Omit when the parent supplies actions outside the form. */
  footerActions?: React.ReactNode;
  showDefaultFooter?: boolean;
  cancelLabel?: string;
  submitLabel?: string;
  onCancel?: () => void;
  onSubmit?: () => void | Promise<void>;
  isSubmitting?: boolean;
  submitPrimary?: boolean;
  /** When true, submit stays enabled regardless of form validation (discontinue modal). */
  allowSubmitWhenInvalid?: boolean;
  /** Discontinue-product modal only: show warning when buyer is missing. */
  showNoBuyerAssignedBanner?: boolean;
  /** Shown in modal when product has no assigned buyer. */
  noBuyerAssignedBannerMessage?: string;
  /** When set (e.g. workflow modals), controls which blocks render. Omitted = all fields visible. */
  sectionVisibility?: Partial<DiscontinuationFormSectionVisibility>;
  /** Warning alert above summary (complete discontinuation modal). */
  warningMessage?: string;
  /** When true, invalid mandatory fields use packaging error styling. */
  showValidationErrors?: boolean;
  /** Buyer return notes from API when workflow is `return_to_cm_discontinued`. */
  buyerNotesForCancelDiscontinuation?: string;
  onBuyerNotesForCancelDiscontinuationChange?: (value: string) => void;
  /** When true, the notes row splits into two columns with buyer notes. */
  showBuyerNotesForCancelDiscontinuation?: boolean;
};

export const createEmptyDiscontinuationInformationFormValues =
  (): DiscontinuationInformationFormValues => ({
    discontinuationDate: "",
    reasonForDiscontinuation: "",
    notes: "",
    documents: [],
  });

/** Snapshot section values for the discontinue-product modal (cancel discards the copy). */
export function cloneDiscontinuationInformationFormValues(
  values: DiscontinuationInformationFormValues
): DiscontinuationInformationFormValues {
  return {
    discontinuationDate: values.discontinuationDate ?? "",
    reasonForDiscontinuation: values.reasonForDiscontinuation ?? "",
    notes: values.notes ?? "",
    documents: (values.documents ?? []).map((doc) => ({ ...doc })),
  };
}

function parseDiscontinuationDate(value: string): Date | undefined {
  return parseDiscontinuationDateValue(value);
}

export function isDiscontinuationInformationFormValid(
  values: DiscontinuationInformationFormValues,
  options?: {
    requireNotes?: boolean;
    requireDocuments?: boolean;
  }
): boolean {
  const errors = getDiscontinuationInformationFieldErrors(values, options);
  return !Object.values(errors).some(Boolean);
}

export type DiscontinuationInformationFieldErrors = {
  discontinuationDate: boolean;
  reasonForDiscontinuation: boolean;
  notes: boolean;
  documents: boolean;
};

export function getDiscontinuationInformationFieldErrors(
  values: DiscontinuationInformationFormValues,
  options?: {
    requireNotes?: boolean;
    requireDocuments?: boolean;
  }
): DiscontinuationInformationFieldErrors {
  const requireDocuments = Boolean(options?.requireDocuments);
  const notesRequired =
    Boolean(options?.requireNotes) ||
    isDiscontinuationReasonOther(values.reasonForDiscontinuation);

  const dateTrimmed = values.discontinuationDate?.trim() ?? "";
  const parsedDate = dateTrimmed ? parseDiscontinuationDateValue(dateTrimmed) : undefined;
  const discontinuationDateInvalid =
    !dateTrimmed ||
    !parsedDate ||
    !isDiscontinuationDateWithinAllowedRange(parsedDate);

  const reasonForDiscontinuationInvalid =
    parseDiscontinuationReasonSelections(values.reasonForDiscontinuation).length === 0;

  const notesInvalid = notesRequired && !values.notes?.trim();

  const documentsInvalid =
    requireDocuments &&
    (!values.documents?.length || values.documents.every((d) => !d?.name));

  return {
    discontinuationDate: discontinuationDateInvalid,
    reasonForDiscontinuation: reasonForDiscontinuationInvalid,
    notes: notesInvalid,
    documents: documentsInvalid,
  };
}

const defaultLabels = {
  discontinuationDate: "Discontinuation Date",
  reasonForDiscontinuation: "Reason For Discontinuation",
  notesForDiscontinuation: "Notes For Discontinuation",
  buyerNotesForCancelDiscontinuation: "Buyer Notes For Cancel Discontinuation",
  documents: "Documents",
  product: "Product",
  vendor: "Vendor",
  buyerName: "Buyer Name",
};

const defaultSectionVisibility: DiscontinuationFormSectionVisibility = {
  productSummary: true,
  warningBanner: false,
  discontinuationDate: true,
  reasonForDiscontinuation: true,
  notes: true,
  documents: true,
};

const DiscontinuationInformationForm: React.FC<DiscontinuationInformationFormProps> = ({
  values,
  onChange,
  reasonOptions,
  layout = "section",
  darkModeClass: darkModeClassProp,
  disabled = false,
  discontinuationDateDisplayMode = "picker",
  requireNotes = false,
  requireDocuments = false,
  productSummary,
  documentsUploadPlaceholder = "UPLOAD DOCUMENT",
  showDocumentUpload = true,
  showDownloadOnDocuments: _showDownloadOnDocuments = false,
  fieldIdPrefix = "discontinuation-information",
  labels: labelsProp,
  footerActions,
  showDefaultFooter = false,
  cancelLabel = "Cancel",
  submitLabel = "Submit",
  onCancel,
  onSubmit,
  isSubmitting = false,
  submitPrimary = true,
  allowSubmitWhenInvalid = false,
  showNoBuyerAssignedBanner = false,
  noBuyerAssignedBannerMessage,
  sectionVisibility: sectionVisibilityProp,
  warningMessage,
  showValidationErrors = false,
  buyerNotesForCancelDiscontinuation = "",
  onBuyerNotesForCancelDiscontinuationChange,
  showBuyerNotesForCancelDiscontinuation = false,
}) => {
  const { darkMode } = useTheme();
  const { languageText } = useLanguageContext();
  const resolvedNoBuyerAssignedBannerMessage =
    noBuyerAssignedBannerMessage ??
    resolveDiscontinuationNoBuyerAssignedBannerMessage(
      languageText?.LABELS?.PACKAGING_AND_LABELLING
    );
  const darkModeClass = darkModeClassProp ?? (darkMode ? "dark" : "light");
  const labels = { ...defaultLabels, ...labelsProp };
  const sectionVisibility = useMemo(
    () => ({ ...defaultSectionVisibility, ...sectionVisibilityProp }),
    [sectionVisibilityProp]
  );

  const maxSelectableDate = useMemo(() => getDiscontinuationMaxSelectableDate(), []);

  const notesRequired =
    requireNotes || isDiscontinuationReasonOther(values.reasonForDiscontinuation);

  const fieldErrors = useMemo(
    () =>
      showValidationErrors
        ? getDiscontinuationInformationFieldErrors(values, {
            requireNotes,
            requireDocuments,
          })
        : {
            discontinuationDate: false,
            reasonForDiscontinuation: false,
            notes: false,
            documents: false,
          },
    [showValidationErrors, values, requireNotes, requireDocuments]
  );

  const [errorHighlightSuppressed, setErrorHighlightSuppressed] = useState<
    Set<keyof DiscontinuationInformationFieldErrors>
  >(() => new Set());

  useEffect(() => {
    if (!showValidationErrors) {
      setErrorHighlightSuppressed(new Set());
    }
  }, [showValidationErrors]);

  const showFieldError = useCallback(
    (key: keyof DiscontinuationInformationFieldErrors) =>
      Boolean(fieldErrors[key]) && !errorHighlightSuppressed.has(key),
    [fieldErrors, errorHighlightSuppressed]
  );

  const suppressFieldErrorHighlight = useCallback(
    (key: keyof DiscontinuationInformationFieldErrors) => {
      setErrorHighlightSuppressed((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    },
    []
  );

  const restoreFieldErrorHighlight = useCallback(
    (key: keyof DiscontinuationInformationFieldErrors) => {
      setErrorHighlightSuppressed((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (discontinuationDateDisplayMode === "text") return;
    if (!sectionVisibility.discontinuationDate) return;
    if (values.discontinuationDate?.trim()) return;
    onChange({
      discontinuationDate: getDefaultDiscontinuationDateString(),
    });
  }, [
    discontinuationDateDisplayMode,
    sectionVisibility.discontinuationDate,
    values.discontinuationDate,
    onChange,
  ]);

  useEffect(() => {
    if (discontinuationDateDisplayMode === "text") return;
    if (!sectionVisibility.discontinuationDate) return;
    if (!values.discontinuationDate?.trim()) return;
    const parsed = parseDiscontinuationDateValue(values.discontinuationDate);
    if (!parsed) return;
    if (isDiscontinuationDateWithinAllowedRange(parsed)) return;
    onChange({
      discontinuationDate: formatDiscontinuationDate(
        clampDiscontinuationDateToAllowedRange(parsed)
      ),
    });
  }, [
    discontinuationDateDisplayMode,
    sectionVisibility.discontinuationDate,
    values.discontinuationDate,
    onChange,
  ]);

  const dateFieldId = `${fieldIdPrefix}-discontinuation-date`;
  const reasonFieldId = `${fieldIdPrefix}-reason`;
  const notesFieldId = `${fieldIdPrefix}-notes`;
  const buyerNotesFieldId = `${fieldIdPrefix}-buyer-notes-for-cancel-discontinuation`;
  const documentsRef = useRef(values.documents);
  documentsRef.current = values.documents;

  const [fileInputKey, setFileInputKey] = useState(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isDiscontinuationUploading, setIsDiscontinuationUploading] = useState(false);

  const validateDiscontinuationFile = useFileValidator({
    allowedTypes: [...FILE_VALIDATION_CONFIG.PACKAGING_ATTACHMENTS.allowedTypes],
    maxSize: DEFAULT_FILE_SIZE,
  });

  const { uploadFile } = useFileManagement({
    productId: "packaging",
    variantId: "discontinuation-documents",
    documentFields: [DISCONTINUATION_UPLOAD_FIELD],
    uploadOptions: {
      maxSize: DEFAULT_FILE_SIZE * 1024 * 1024,
      allowedTypes: [],
      allowedExtensions: [],
    },
  });

  const uploadDocuments: DocumentsUploadItem[] = useMemo(
    () =>
      (values.documents ?? [])
        .filter((d) => d?.id != null && d?.name)
        .map((d) => ({
          id: d.id as string | number,
          fileName: String(d.name),
        })),
    [values.documents]
  );

  const handleDiscontinuationFileChange = useCallback(
    async (file: File | null) => {
      if (!file || disabled || isSubmitting || !showDocumentUpload) {
        return;
      }
      if (!validateDiscontinuationFile(file)) {
        setPendingFile(null);
        setFileInputKey((k) => k + 1);
        return;
      }

      const tempId = `temp-discontinuation-${Date.now()}-${Math.random()}`;
      setPendingFile(file);
      const withTemp: DiscontinuationDocumentItem[] = [
        ...(documentsRef.current ?? []),
        { id: tempId, name: file.name },
      ];
      documentsRef.current = withTemp;
      onChange({ documents: withTemp });
      setFileInputKey((k) => k + 1);
      setPendingFile(null);
      setIsDiscontinuationUploading(true);

      try {
        const uploadResult = await uploadFile(
          DISCONTINUATION_UPLOAD_FIELD,
          file,
          `packaging/discontinuation/${Date.now()}`
        );
        if (uploadResult?.id) {
          const updated = (documentsRef.current ?? []).map((doc) =>
            doc.id === tempId
              ? {
                  id: uploadResult.id,
                  name: uploadResult.filename ?? file.name,
                }
              : doc
          );
          documentsRef.current = updated;
          onChange({ documents: updated });
        }
      } catch {
        const rolledBack = (documentsRef.current ?? []).filter(
          (doc) => doc.id !== tempId
        );
        documentsRef.current = rolledBack;
        onChange({ documents: rolledBack });
        setFileInputKey((k) => k + 1);
      } finally {
        setIsDiscontinuationUploading(false);
      }
    },
    [
      disabled,
      isSubmitting,
      showDocumentUpload,
      validateDiscontinuationFile,
      onChange,
      uploadFile,
    ]
  );

  const handleDiscontinuationDocumentDelete = useCallback(
    (documentId: string) => {
      if (disabled || isSubmitting) return;
      const without = (documentsRef.current ?? []).filter(
        (d) => String(d.id) !== documentId
      );
      documentsRef.current = without;
      onChange({ documents: without });
    },
    [disabled, isSubmitting, onChange]
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (!date || !isValid(date)) {
        onChange({ discontinuationDate: "" });
        return;
      }
      const clamped = clampDiscontinuationDateToAllowedRange(date);
      onChange({
        discontinuationDate: formatDiscontinuationDate(clamped),
      });
    },
    [onChange]
  );

  const blockSubmitWithoutAssignedBuyer =
    showNoBuyerAssignedBanner &&
    productSummary != null &&
    !hasDiscontinuationSummaryText(productSummary.buyerName);

  const submitDisabled =
    blockSubmitWithoutAssignedBuyer ||
    (allowSubmitWhenInvalid
      ? isSubmitting
      : !isDiscontinuationInformationFormValid(values, {
          requireNotes,
          requireDocuments,
        }) || isSubmitting);

  const reasonMultipleSelectValue = useMemo(
    () =>
      formatValuesForMultipleSelect(
        parseDiscontinuationReasonSelections(values.reasonForDiscontinuation),
        reasonOptions
      ),
    [values.reasonForDiscontinuation, reasonOptions]
  );

  const reasonField = (
    <Box>
      <Label
        text={labels.reasonForDiscontinuation}
        htmlFor={reasonFieldId}
        className={`${darkModeClass} ${
          showFieldError("reasonForDiscontinuation") ? "label-error-text" : ""
        }`}
        required
      />
      <MultipleSelect
        options={reasonOptions}
        value={reasonMultipleSelectValue}
        onChange={(selected) => {
          const nextValues = Array.isArray(selected)
            ? selected
                .map((item) =>
                  item?.value != null ? String(item.value) : String(item?.label ?? "")
                )
                .filter(Boolean)
            : [];
          onChange({
            reasonForDiscontinuation: serializeDiscontinuationReasonSelections(nextValues),
          });
        }}
        className={`${darkModeClass} ${
          showFieldError("reasonForDiscontinuation") ? "field-error-color-multiple-select" : ""
        }`}
        disabled={disabled || isSubmitting}
        onFocus={() => suppressFieldErrorHighlight("reasonForDiscontinuation")}
        onBlur={() => restoreFieldErrorHighlight("reasonForDiscontinuation")}
      />
    </Box>
  );

  const dateField = (
    <Box>
      <Label
        text={labels.discontinuationDate}
        htmlFor={dateFieldId}
        className={`${darkModeClass} ${
          showFieldError("discontinuationDate") ? "label-error-text" : ""
        }`}
        required={discontinuationDateDisplayMode !== "text"}
      />
      {discontinuationDateDisplayMode === "text" ? (
        <Input
          id={dateFieldId}
          className={darkModeClass}
          value={values.discontinuationDate?.trim() || "-"}
          disabled
        />
      ) : (
        <CustomDatePicker
          id={dateFieldId}
          selected={parseDiscontinuationDate(values.discontinuationDate)}
          onChange={handleDateChange}
          dateFormat={DISCONTINUATION_DATE_FORMAT}
          className={`${darkModeClass} ${
            showFieldError("discontinuationDate") ? "field-error-color" : ""
          }`}
          placeholder="Select date"
          disabled={disabled || isSubmitting}
          maxDate={maxSelectableDate}
        />
      )}
    </Box>
  );

  const dateReasonRow =
    sectionVisibility.discontinuationDate || sectionVisibility.reasonForDiscontinuation ? (
      layout === "modal" ? (
        <Box className="grid grid-cols-1 gap-[1.5rem]">
          {sectionVisibility.discontinuationDate ? dateField : null}
          {sectionVisibility.reasonForDiscontinuation ? reasonField : null}
        </Box>
      ) : (
        <Box className="grid grid-cols-1 sm:grid-cols-2 gap-[1.5rem]">
          {sectionVisibility.reasonForDiscontinuation ? reasonField : null}
          {sectionVisibility.discontinuationDate ? dateField : null}
        </Box>
      )
    ) : null;

  const showSummary =
    sectionVisibility.productSummary && productSummary != null;

  const defaultFooter =
    showDefaultFooter && (onCancel || onSubmit) ? (
      <Box display="flex" justifyContent="flex-end" gap={2} pt={3}>
        {onCancel ? (
          <Button
            className={darkModeClass}
            size="large"
            text={cancelLabel}
            onClick={onCancel}
            disabled={isSubmitting}
          />
        ) : null}
        {onSubmit ? (
          <Button
            className={darkModeClass}
            size="large"
            text={submitLabel}
            primary={submitPrimary}
            disabled={submitDisabled}
            onClick={onSubmit}
          />
        ) : null}
      </Box>
    ) : null;

  return (
    <Box className="flex flex-col gap-[1.5rem]">
      {sectionVisibility.warningBanner && warningMessage ? (
        <Box sx={{ "& .alert": { margin: 0 } }}>
          <Alert severity="warning" message={warningMessage} />
        </Box>
      ) : null}

      {showSummary ? (
        <DiscontinuationProductSummaryView
          productSummary={productSummary}
          darkMode={darkMode}
          labels={labels}
          showNoBuyerAssignedBanner={showNoBuyerAssignedBanner}
          noBuyerAssignedBannerMessage={resolvedNoBuyerAssignedBannerMessage}
          showBuyerName={sectionVisibility.productSummaryShowBuyerName ?? true}
        />
      ) : null}

      {dateReasonRow}

      {sectionVisibility.notes ? (
        showBuyerNotesForCancelDiscontinuation ? (
          <Box className="grid grid-cols-1 sm:grid-cols-2 gap-[1.5rem]">
            <Box>
              <Label
                text={labels.notesForDiscontinuation}
                htmlFor={notesFieldId}
                className={`${darkModeClass} ${showFieldError("notes") ? "label-error-text" : ""}`}
                required={notesRequired}
              />
              <Textarea
                id={notesFieldId}
                className={`${darkModeClass} ${showFieldError("notes") ? "field-error-color" : ""}`}
                value={values.notes}
                disabled={disabled || isSubmitting}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    notes:
                      v.length > NOTES_MAX_LENGTH ? v.slice(0, NOTES_MAX_LENGTH) : v,
                  });
                }}
                maxLength={NOTES_MAX_LENGTH}
              />
            </Box>
            <Box>
              <Label
                text={labels.buyerNotesForCancelDiscontinuation}
                htmlFor={buyerNotesFieldId}
                className={darkModeClass}
              />
              <Textarea
                id={buyerNotesFieldId}
                className={darkModeClass}
                value={buyerNotesForCancelDiscontinuation ?? ""}
                disabled={disabled || isSubmitting}
                onChange={(e) => {
                  const v = e.target.value;
                  onBuyerNotesForCancelDiscontinuationChange?.(
                    v.length > NOTES_MAX_LENGTH ? v.slice(0, NOTES_MAX_LENGTH) : v
                  );
                }}
                maxLength={NOTES_MAX_LENGTH}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            <Label
              text={labels.notesForDiscontinuation}
              htmlFor={notesFieldId}
              className={`${darkModeClass} ${showFieldError("notes") ? "label-error-text" : ""}`}
              required={notesRequired}
            />
            <Textarea
              id={notesFieldId}
              className={`${darkModeClass} ${showFieldError("notes") ? "field-error-color" : ""}`}
              value={values.notes}
              disabled={disabled || isSubmitting}
              onChange={(e) => {
                const v = e.target.value;
                onChange({
                  notes:
                    v.length > NOTES_MAX_LENGTH ? v.slice(0, NOTES_MAX_LENGTH) : v,
                });
              }}
              maxLength={NOTES_MAX_LENGTH}
            />
          </Box>
        )
      ) : null}

      {sectionVisibility.documents ? (
        <Box>
          <Label
            text={labels.documents}
            className={`${darkModeClass} ${showFieldError("documents") ? "label-error-text" : ""}`}
            required={requireDocuments}
          />
          <DocumentsUploadList
            documents={uploadDocuments}
            onFileChange={handleDiscontinuationFileChange}
            onDelete={handleDiscontinuationDocumentDelete}
            disabled={disabled || isSubmitting || !showDocumentUpload}
            isReadOnly={disabled || isSubmitting || !showDocumentUpload}
            isUploading={isDiscontinuationUploading}
            fileInputKey={fileInputKey}
            fileInputValue={pendingFile}
            setFileInputValue={setPendingFile}
            placeholder={documentsUploadPlaceholder}
            allowedTypes={[...FILE_VALIDATION_CONFIG.PACKAGING_ATTACHMENTS.allowedTypes]}
            toastMessage={FILE_VALIDATION_CONFIG.PACKAGING_ATTACHMENTS.toastMessage}
            fileInputError={showFieldError("documents")}
          />
        </Box>
      ) : null}

      {footerActions ?? defaultFooter}
    </Box>
  );
};

export default DiscontinuationInformationForm;
